import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  MapContainer,
  FeatureGroup,
  useMap,
  Polygon,
  Marker,
  Popup,
  Polyline
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { Form, Modal, Button } from 'react-bootstrap';
import L from 'leaflet';
import 'leaflet-boundary-canvas';
import * as turf from '@turf/turf';
import PropTypes from 'prop-types';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { useSelector, useDispatch } from 'react-redux';
import { calculatePolygonAreaHa } from 'helpers/gis';
import {
  addDefinedPropertyBoundary,
  addPlantingSites,
  addFence,
  addTreeMarkers,
  setPlantingSiteFocus,
  setShowEditControl,
  addSurroundingProperties
} from 'reducers/livingCarbonGrantReducer';
import SiteNameModal from './requirements/SiteNameModal';
import ReactDOMServer from 'react-dom/server';
import { ReactComponent as OakSvg } from '../../../../../src/assets/icons/oak.svg';

const purpleOptions = { color: 'purple', fillOpacity: 0.1 };

// north coast, mid coast, riverina
const regionBoundary = [
  [
    [-30.661606650594525, 151.50493499776636],
    [-30.661606650594525, 151.50493499776636],
    [-30.623787488075852, 151.7577925565861],
    [-30.623787488075852, 151.7577925565861],
    [-30.661606650594525, 151.93369346706945],
    [-30.661606650594525, 151.93369346706945],
    [-30.7655330354981, 152.32947051565694],
    [-30.7655330354981, 152.32947051565694],
    [-30.88821058600352, 152.79121040567566],
    [-30.88821058600352, 152.79121040567566],
    [-30.907070135701943, 152.91214228163295],
    [-30.907070135701943, 152.91214228163295],
    [-30.680510684887548, 152.92313608853814],
    [-30.680510684887548, 152.92313608853814],
    [-30.244784577658155, 153.0440679644954],
    [-30.244784577658155, 153.0440679644954],
    [-29.988127387728856, 153.1759936473579],
    [-29.988127387728856, 153.1759936473579],
    [-29.702172733503513, 153.3079193302204],
    [-29.702172733503513, 153.3079193302204],
    [-29.223769756253837, 153.31891313712563],
    [-29.223769756253837, 153.31891313712563],
    [-28.9741114738964, 153.4508388199881],
    [-28.9741114738964, 153.4508388199881],
    [-28.72384937250459, 153.51680166141932],
    [-28.72384937250459, 153.51680166141932],
    [-28.298962680138388, 153.51680166141932],
    [-28.298962680138388, 153.51680166141932],
    [-28.153724799740484, 153.46183262689334],
    [-28.153724799740484, 153.46183262689334],
    [-28.337659376444574, 153.0440679644954],
    [-28.337659376444574, 153.0440679644954],
    [-28.318312788680224, 152.64829091590798],
    [-28.318312788680224, 152.64829091590798],
    [-28.327986522757413, 152.35145812946735],
    [-28.327986522757413, 152.35145812946735],
    [-28.54058558851557, 151.9996563085007],
    [-28.54058558851557, 151.9996563085007],
    [-28.80091720933906, 152.07661295683715],
    [-28.80091720933906, 152.07661295683715],
    [-28.983724901929694, 151.92269966016426],
    [-28.983724901929694, 151.92269966016426],
    [-29.36752743386188, 151.87872443254344],
    [-29.36752743386188, 151.87872443254344],
    [-29.778507335661573, 151.80176778420693],
    [-29.778507335661573, 151.80176778420693],
    [-30.27326083367496, 151.55990403229242],
    [-30.27326083367496, 151.55990403229242],
    [-30.51024134548037, 151.53791641848198],
    [-30.51024134548037, 151.53791641848198],
    [-30.65215324645601, 151.5269226115768],
    [-30.65215324645601, 151.5269226115768],
    [-30.65215324645601, 151.82375539801737],
    [-30.65215324645601, 151.82375539801737]
  ],
  [
    [-31.00888854932193, 152.94486942382147],
    [-30.95235900140892, 152.59306760285483],
    [-30.76368565435742, 151.86747634711116],
    [-30.782569686078862, 151.29579838804034],
    [-30.990049087621045, 150.9439965670737],
    [-31.759400760254298, 150.7680956565904],
    [-32.35546780152703, 151.09790986374662],
    [-32.670543408288786, 151.69157543662783],
    [-32.41114958289585, 152.26325339569865],
    [-31.9647366142729, 152.41716669237155],
    [-31.722017869320005, 152.76896851333817]
  ],
  [
    [-34.80262319176898, 143.78800799199075],
    [-35.01890429207272, 143.77701418508556],
    [-35.18972226707838, 143.82098941270638],
    [-35.28845314085714, 144.8654010687011],
    [-35.25256491177506, 145.8108684625489],
    [-35.53922508861833, 146.34956500090414],
    [-35.65538938443305, 146.77832347020723],
    [-35.601796044367454, 147.14111909807906],
    [-35.35121927254865, 147.20708193951032],
    [-35.10885311329377, 147.37198904308843],
    [-35.144804881050824, 147.64683421571863],
    [-34.901822753683355, 147.74577847786546],
    [-34.350212004525375, 147.4379518845197],
    [-34.214009297745946, 147.22906955332073],
    [-34.3139128672818, 146.96521818759575],
    [-34.52241823223913, 146.84428631163846],
    [-34.49525137426549, 146.52546591138747],
    [-34.223096343163434, 146.16267028351558],
    [-34.223096343163434, 145.65695516587604],
    [-34.29575740976582, 145.39310380015107],
    [-34.36835568230957, 144.8324196479855],
    [-34.36835568230957, 144.24974788200947],
    [-34.35928433442165, 143.7110513436543],
    [-34.64003888855629, 143.70005753674906],
    [-34.81164627511845, 144.1617974267678],
    [-34.88379536690433, 143.93092748175846]
  ]
];

const MapUpdateLocation = ({ centerCoor, layer }) => {
  let osm = null; // useState does not work here somehow
  const map = useMap();
  const dispatch = useDispatch();
  const {
    currentState,
    selectedLandParcels,
    plantingSites,
    regionFocus,
    plantingSiteFocus
  } = useSelector(state => state.livingCarbonGrant);

  const fetchGeoJSON = async () => {
    osm = L.TileLayer.boundaryCanvas(
      process.env.REACT_APP_LEAFLET_OPENSM_STANDARD_TILE_LAYER_URL,
      {
        // boundary: geoJSONData, // what does this do?
        attribution:
          '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors, UK shape <a href="https://github.com/johan/world.geo.json">johan/word.geo.json</a>'
      }
    );
  };

  useEffect(() => {
    if (!map) return;
    fetchGeoJSON().then(() => {
      map.addLayer(osm);
    });

    switch (currentState) {
      case 1:
        if (selectedLandParcels.length > 0) {
          // const geoJSONData = layer.toGeoJSON();
          map.fitBounds(layer.getBounds());
        } else {
          if (regionFocus === -1) {
            map.fitBounds(regionBoundary); // default view to fit all 3 region
          } else {
            map.fitBounds(regionBoundary[regionFocus]);
          }
        }
        break;
      case 2:
        for (let i = 0; i < plantingSites.length; i++) {
          if (plantingSites[i].id === plantingSiteFocus) {
            map.fitBounds(plantingSites[i].coordinates);
            dispatch(setPlantingSiteFocus(-1));
          }
        }
        break;
      default:
        break;
    }
  }, [centerCoor, map, layer, regionFocus, plantingSiteFocus]);

  return null;
};

MapUpdateLocation.propTypes = {
  centerCoor: PropTypes.array.isRequired,
  layer: PropTypes.object
};

const DraggableMarker = ({ marker }) => {
  const [position, setPosition] = useState([marker.lat, marker.lng]);
  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const lat = marker.getLatLng().lat;
          const lng = marker.getLatLng().lng;
          setPosition([lat, lng]);
        }
      }
    }),
    []
  );

  const getIcon = () => {
    return new L.DivIcon({
      html: ReactDOMServer.renderToString(
        <OakSvg className="land-dev-tree-icon" />
      ),
      className: 'oak'
    });
  };

  return (
    <Marker
      key={marker.id}
      position={position}
      icon={getIcon(0)}
      draggable={true}
      eventHandlers={eventHandlers}
      ref={markerRef}
    >
      <Popup>
        <h5>ID: {marker.id}</h5>
        <p>
          Coordinates: {position[0]}, {position[1]}
        </p>
      </Popup>
    </Marker>
  );
};

DraggableMarker.propTypes = {
  marker: PropTypes.shape({
    id: PropTypes.string.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    getLatLng: PropTypes.func.isRequired
  })
};

const RegionMap = ({ onEditMounted, itemType }) => {
  const dispatch = useDispatch();
  const {
    position,
    currentState,
    selectedLandParcels,
    plantingSites,
    fence,
    drawingFence,
    showEditControl,
    developPlantingSite,
    treeMarkers,
    surroundingProperties
  } = useSelector(state => state.livingCarbonGrant);
  const [layer, setLayer] = useState(null);
  const [quantity, setQuantity] = useState(10);
  const [showSiteNameModal, setShowSiteNameModal] = useState(false);
  const [showAddTreeConfirmationModal, setShowAddTreeConfirmationModal] =
    useState(false);
  const [customPolygonStyle, setCustomPolygonStyle] = useState({});
  const [latestCoordinates, setLatestCoordinates] = useState([]); // captures the latest polygon drawn

  const handlePolygonCreate = ({ layer }) => {
    dispatch(setShowEditControl(false));
    setLayer(layer);
    let coordinates = layer.getLatLngs()[0].map(p => [p.lat, p.lng]);
    coordinates = [...coordinates, coordinates[0]]; // add starting coordinate again to close the polygon

    const calculatedArea = calculatePolygonAreaHa(coordinates);

    if (calculatedArea > 0) {
      switch (currentState) {
        case 1: // defining land boundary
          // curly braces needed, otherwise unable to use "let" inside the case
          {
            const bounds = layer.getBounds();
            const xMin = bounds.getEast();
            const xMax = bounds.getWest();
            const yMin = bounds.getSouth();
            const yMax = bounds.getNorth();
            let vertices = null;

            if (layer.getLatLngs()[0]) {
              const outerRing = layer.getLatLngs()[0];
              vertices = outerRing.map(coordinate => ({
                lat: coordinate.lat,
                lng: coordinate.lng,
                key: Date.now()
              }));
            }
            dispatch(
              addDefinedPropertyBoundary({
                id: Date.now(),
                parcelId: Date.now(),
                coordinates: coordinates,
                area: calculatedArea,
                vertices,
                xMin,
                xMax,
                yMin,
                yMax
              })
            );
          }
          break;

        case 2:
          setLatestCoordinates(coordinates);
          if (developPlantingSite === -1 && itemType === 1) {
            // defining planting sites boundary
            setShowSiteNameModal(true);
          } else if (itemType === 5) {
            dispatch(
              addSurroundingProperties({
                id: Date.now(),
                coordinates: latestCoordinates,
                area: calculatePolygonAreaHa(latestCoordinates),
                trees: []
              })
            );
          } else {
            // defining trees boundary (planting trees)
            setShowAddTreeConfirmationModal(true); // handleAddTrees() runs eventually
          }
          break;

        default:
          break;
      }
    } else {
      layer.remove();
    }
  };

  // for fence
  const handleLineCreate = ({ layer }) => {
    dispatch(setShowEditControl(false));
    setLayer(layer);
    let coordObj = layer.getLatLngs(); // array of objects [{}, {} ..]
    let coordinates = coordObj.map(p => [p.lat, p.lng]); // array of array [[], [] ..]

    // get distance
    let dist = 0;
    for (let i = 0; i < coordObj.length - 1; i++) {
      dist += coordObj[i].distanceTo(coordObj[i + 1]);
    }

    dispatch(
      addFence({
        id: Date.now(),
        coordinates: coordinates,
        type: drawingFence, // 0 - new, 1 - existing
        dist: dist
      })
    );
  };

  const toggleSiteNameModal = () => {
    setShowSiteNameModal(prevVisible => !prevVisible);
  };

  const toggleShowAddTreeConfirmationModal = () => {
    setShowAddTreeConfirmationModal(prevVisible => !prevVisible);
  };

  const handleSiteName = value => {
    setShowSiteNameModal(false);
    dispatch(
      addPlantingSites({
        id: Date.now(),
        name: value,
        coordinates: latestCoordinates,
        area: calculatePolygonAreaHa(latestCoordinates),
        trees: []
      })
    );
  };

  const handleAddTrees = () => {
    toggleShowAddTreeConfirmationModal();

    const polygon = L.polygon(latestCoordinates);
    const bounds = polygon.getBounds();
    const x_min = bounds.getEast();
    const x_max = bounds.getWest();
    const y_min = bounds.getSouth();
    const y_max = bounds.getNorth();

    let pointCount = 0;
    let gridCount = Math.ceil(Math.sqrt(quantity));

    for (let i = 0; i < gridCount; i++) {
      for (let j = 0; j < gridCount; j++) {
        if (pointCount >= quantity) {
          break; // stop create point when reach quantity
        }

        const lat = y_min + ((y_max - y_min) * i) / gridCount;
        const lng = x_min + ((x_max - x_min) * j) / gridCount;

        const turfPoint = turf.point([lng, lat]);
        const poly = polygon.toGeoJSON();
        const turfInside = turf.booleanPointInPolygon(turfPoint, poly);
        const timestamp = Date.now();

        if (turfInside) {
          const points = {
            id: `${pointCount}${timestamp}`,
            plantingSiteId: developPlantingSite,
            treeType: developPlantingSite,
            lat: lat,
            lng: lng
          };

          dispatch(addTreeMarkers(points));
          pointCount++;
        }
      }
    }
    if (pointCount < quantity) {
      // create more point to reach quantity
      for (let i = pointCount; i < quantity; i++) {
        const randomLat = y_min + Math.random() * (y_max - y_min);
        const randomLng = x_min + Math.random() * (x_max - x_min);
        const poly = polygon.toGeoJSON();

        const randomTurfPoint = turf.point([randomLng, randomLat]);
        const randomTurfInside = turf.booleanPointInPolygon(
          randomTurfPoint,
          poly
        );

        if (randomTurfInside) {
          const randomTimestamp = Date.now();
          const randomPoints = {
            id: `${i}${randomTimestamp}`,
            plantingSiteId: developPlantingSite,
            treeType: developPlantingSite,
            lat: randomLat,
            lng: randomLng
          };

          dispatch(addTreeMarkers(randomPoints));
        } else if (!randomTurfInside && quantity <= 10) {
          i--; //Ensure to recreate the point if it is not inside the polygon.
        }
      }
    }
  };

  useEffect(() => {
    let customize = {};
    switch (itemType) {
      case 1: // planting site
        customize = {
          color: 'yellow'
        };
        setCustomPolygonStyle(customize);
        break;
      case 2: // photo monitoring point
        customize = {
          color: 'red'
        };
        setCustomPolygonStyle(customize);
        break;
      case 3: // new fence
        customize = {
          color: 'blue'
        };
        setCustomPolygonStyle(customize);
        break;
      case 4: // exisiting fence
        customize = {
          color: 'green'
        };
        setCustomPolygonStyle(customize);
        break;
      case 5: // surrounding properties
        customize = {
          color: 'white'
        };
        setCustomPolygonStyle(customize);
        break;
      default:
        return;
    }
  }, [itemType]);

  return (
    <>
      <MapContainer center={position} zoom={6} className="h-100">
        <MapUpdateLocation centerCoor={position} layer={layer} />

        {currentState === 1 && (
          <>
            <Polygon pathOptions={purpleOptions} positions={regionBoundary} />
            <FeatureGroup>
              <EditControl
                onMounted={onEditMounted}
                position="topright"
                edit={{
                  edit: false,
                  poly: true
                }}
                onCreated={handlePolygonCreate}
                draw={{
                  rectangle: true,
                  polyline: false,
                  circle: false,
                  marker: false,
                  circlemarker: false,
                  polygon: {
                    shapeOptions: {
                      color: 'green'
                    }
                  }
                }}
              ></EditControl>
            </FeatureGroup>
          </>
        )}

        {currentState === 2 && (
          <>
            {showEditControl && (
              <FeatureGroup>
                {/* Planting Site or Surrounding properties*/}
                {itemType === 1 || itemType === 5 || itemType === 6 ? (
                  <EditControl
                    onMounted={onEditMounted}
                    position="topright"
                    edit={{
                      edit: false,
                      poly: true
                    }}
                    onCreated={handlePolygonCreate}
                    // onDrawStop={handleDrawStop}
                    draw={{
                      rectangle: {
                        shapeOptions: customPolygonStyle,
                        showLength: true
                      },
                      polyline: false,
                      circle: false,
                      marker: false,
                      circlemarker: false,
                      polygon: {
                        shapeOptions: customPolygonStyle,
                        showLength: true
                      }
                    }}
                  ></EditControl>
                ) : (
                  <EditControl
                    onMounted={onEditMounted}
                    onCreated={handleLineCreate}
                    position="topright"
                    edit={{
                      edit: false,
                      poly: true
                    }}
                    draw={{
                      rectangle: false,
                      polyline: {
                        shapeOptions: customPolygonStyle,
                        showLength: true
                      },
                      circle: false,
                      marker: false,
                      circlemarker: false,
                      polygon: false
                    }}
                  ></EditControl>
                )}
              </FeatureGroup>
            )}

            {developPlantingSite === -1 ? (
              <>
                {selectedLandParcels &&
                  selectedLandParcels.map(parcel => {
                    return (
                      <Polygon
                        key={parcel.id}
                        pathOptions={{ color: 'blue', fillOpacity: 0 }}
                        positions={parcel.coordinates}
                        // eventHandlers={
                        //   currentState === 1
                        //     ? {
                        //       mouseover: e =>
                        //         e.target.setStyle({ color: 'green' }),
                        //       mouseout: e =>
                        //         e.target.setStyle({ color: 'blue' })
                        //     }
                        //     : null
                        // }
                        weight={1}
                      ></Polygon>
                    );
                  })}
                {plantingSites.map(parcel => {
                  return (
                    <Polygon
                      key={parcel.id}
                      pathOptions={{ color: 'yellow' }}
                      positions={parcel.coordinates}
                      eventHandlers={{
                        mouseover: e => e.target.setStyle({ color: 'green' }),
                        mouseout: e => e.target.setStyle({ color: 'yellow' })
                      }}
                      weight={1}
                    ></Polygon>
                  );
                })}
                {fence.map(parcel => {
                  return (
                    <Polyline
                      key={parcel.id}
                      pathOptions={
                        parcel.type === 0
                          ? { color: 'blue', opacity: 0.5 }
                          : { color: 'green', opacity: 0.5 }
                      }
                      positions={parcel.coordinates}
                      weight={3}
                    ></Polyline>
                  );
                })}
                {surroundingProperties.map(parcel => {
                  return (
                    <Polygon
                      key={parcel.id}
                      pathOptions={{ color: 'white' }}
                      positions={parcel.coordinates}
                      eventHandlers={{
                        mouseover: e => e.target.setStyle({ color: 'green' }),
                        mouseout: e => e.target.setStyle({ color: 'white' })
                      }}
                      weight={1}
                    ></Polygon>
                  );
                })}
              </>
            ) : (
              <>
                {plantingSites.map(parcel => {
                  return (
                    <Polygon
                      key={parcel.id}
                      pathOptions={{ color: 'green', fillOpacity: 0 }}
                      positions={parcel.coordinates}
                      weight={2}
                    ></Polygon>
                  );
                })}
              </>
            )}

            {treeMarkers &&
              treeMarkers.map(marker => (
                <DraggableMarker key={marker.id} marker={marker} />
              ))}
          </>
        )}
      </MapContainer>

      <SiteNameModal
        show={showSiteNameModal}
        onHide={toggleSiteNameModal}
        handleSiteName={handleSiteName}
      />

      <Modal
        show={showAddTreeConfirmationModal}
        onHide={toggleShowAddTreeConfirmationModal}
        aria-labelledby="side-name-modal"
        backdrop="static"
        centered
      >
        <Modal.Header>
          <Modal.Title id="side-name-modal">Tree options</Modal.Title>
          <FalconCloseButton
            onClick={() => {
              toggleShowAddTreeConfirmationModal();
            }}
          />
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Quantity</Form.Label>
          <Form.Control
            type="number"
            placeholder="0"
            onChange={event => {
              setQuantity(event.target.value);
            }}
            value={quantity}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            type="submit"
            className="mt-3"
            onClick={() => handleAddTrees()}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

RegionMap.propTypes = {
  onEditMounted: PropTypes.func,
  itemType: PropTypes.number
};

export default RegionMap;
